import type { Permissions } from 'types'
import { ROUTES } from './routes'

/*
  WHICH PERMISSION GATES WHICH `/internal/*` PAGE — ONE TABLE, TWO CONSUMERS (VRG-410).

  `InternalRoute` reads it to decide whether a URL may open, and `Sidebar` reads it to
  decide whether a nav row renders. They MUST read the same thing: the failure this
  table exists to prevent is a menu and a URL disagreeing, which is what produced the
  original defect — `InternalRoute` admitted exactly two role names while the seeded
  grants had moved on, so `vera_fulfillment` held every permission the fulfilment
  pipeline needs and could not open a single page that uses them.

  THE RULE IS DERIVED FROM THE API, NOT INVENTED. Each entry cites the controller that
  actually enforces the page's data calls, so "which permission gates this page" has one
  answer and it is checkable. The backend `@Permissions` guard remains the real security
  boundary (§27); this is the convenience layer that stops someone landing on a page
  whose every request 403s.

  TWO SHAPES, AND THE SECOND ONE IS A CONFESSION RATHER THAN A DESIGN.

    { permission }  the page's API calls carry `@Permissions(...)`. Normal case.
    { roles }       the API gates that page by ROLE LIST ONLY — there is no permission
                    in the catalog to express it. Recording the role list here keeps the
                    table honest instead of silently letting the page through; each such
                    entry names the decision that would remove it.

  A path with NO entry is reachable by any internal role (see `INTERNAL_ROLE_NAMES`).
  That is the right default for pages with no cross-company data behind them — the docs
  site is the live example.
*/

export type InternalAccessRule = {
  /**
   * Permission the page's own API calls require. `null` when the API gates the page by
   * role alone, or when there is nothing cross-company behind it.
   */
  permission: Permissions | null
  /**
   * ANY-OF. The page opens if the user holds at least ONE of these.
   *
   * A third shape, and like `roles` it is a confession rather than a preference — but a
   * narrower one. It exists for a page whose TABS carry different permissions and each
   * gates itself, so the route's job is only to answer "is there anything here for you"
   * rather than "may you see all of it".
   *
   * `/internal/usage` is the case: Tours reads `internal_analytics_view`
   * (platform_admin only) and Ask reads `internal_support_manage` (platform_admin AND
   * platform_manager). A single `permission` would have to pick one, and either choice
   * is wrong — `internal_analytics_view` locks platform_manager out of a tab it is
   * explicitly granted, `internal_support_manage` admits a hypothetical holder to a page
   * whose other tab they cannot read.
   *
   * IT GRANTS NOTHING ON ITS OWN. Opening the page is not seeing its contents: each tab
   * still checks its own permission and the API still enforces `@Permissions` (§27). A
   * platform_manager reaching `/internal/usage` finds the Ask tab and no Tours tab.
   * Use it ONLY for that pattern; a page with one permission takes `permission`.
   */
  anyPermission?: readonly Permissions[]
  /**
   * Role names the API restricts the page to, for the entries where no permission
   * exists. Checked against the RAW role name from the auth profile — see
   * `useIsInternalUser` for why the mapped `profile.role.name` cannot be used here.
   */
  roles?: readonly string[]
  /**
   * `true` when the rule matches only this exact path, not its descendants. Needed for
   * `/internal`, which is a prefix of every other entry.
   */
  exact?: true
}

type InternalAccessEntry = InternalAccessRule & { path: string }

/*
  Ordered for reading, not for matching — `ruleForInternalPath` sorts by specificity.
*/
const INTERNAL_ACCESS_ENTRIES: readonly InternalAccessEntry[] = [
  // ── The console index ──────────────────────────────────────────────────────
  // `/internal` redirects to Analytics → Website (VRG-684; it used to render that page), so
  // it takes Website's rule and a role that cannot open Website is not bounced through it.
  // EXACT, because it is a string prefix of every other path in this table.
  { path: ROUTES.INTERNAL, permission: 'internal_analytics_view', exact: true },

  // ── Analytics (was "Insights"; VRG-684) ─────────────────────────────────────
  // Website (`/internal/analytics/website`) — and `/internal/analytics` itself, which now
  // redirects there — takes this entry by segment match. VRG-640 put the vera.inc website funnel here
  // (`GET internal/website-analytics/*`, `@Permissions(InternalAnalyticsView)`); ROI by
  // company and sales over time are still to come. The permission is settled:
  // `internal_analytics_view`, the same grant Tours used to sit behind here. Visitor
  // history + Leads (emails) are additionally platform_admin-only, enforced by the API's
  // role list — the page hides them, the 403 is the control.
  { path: ROUTES.INTERNAL_ANALYTICS, permission: 'internal_analytics_view' },

  // Product (`/internal/analytics/product`, VRG-684; was `/internal/usage`, whose entry stays
  // below for the redirect): Tours + Ask, BOTH on `internal_analytics_view` since VRG-685.
  //   Tours -> `GET internal/tour-analytics`, `@Permissions(InternalAnalyticsView)`
  //   Ask   -> `GET internal/ask-stats`,      `@Permissions(InternalAnalyticsView)`
  //
  // SPENCER, 2026-10-08 (VRG-685): platform_manager does not see the Analytics section AT ALL,
  // for now — neither Website nor Product. Until then this entry was `anyPermission:
  // [internal_analytics_view, internal_support_manage]`, so a manager reached Product for the
  // Ask tab through the support-queue grant. That path is removed here AND on the API
  // (`ask-stats-internal.controller.ts`), so a manager gets a 403, not merely a hidden tab.
  // `internal_support_manage` itself is unchanged: managers keep the Support page. A per-company
  // "account manager" scoping was proposed and deferred by Spencer.
  //
  // Explicit rather than inherited from `/internal/analytics` above, so the decision is stated
  // where a reader looks for it; `analytics-manager-access.guard.spec.ts` pins it.
  { path: ROUTES.INTERNAL_ANALYTICS_PRODUCT, permission: 'internal_analytics_view' },
  // The old Usage address redirects to Product; same rule.
  { path: ROUTES.INTERNAL_USAGE, permission: 'internal_analytics_view' },

  // ── GTM ────────────────────────────────────────────────────────────────────
  // VRG-684 — ONE entry for the whole group: `internal_gtm_view` ("View GTM"), seeded to
  // platform_admin. It matches by path SEGMENT, so it covers every page under it — Channels,
  // SEO & AEO, Articles, Sales (Pipeline, Deals, Companies, Leads, Campaigns, Playbook, Templates,
  // and a campaign at /internal/gtm/sales/campaigns/:id), Trade shows, Affiliates — and the
  // redirect-only old addresses (/internal/gtm/outreach, /internal/gtm/sales/outreach,
  // /internal/gtm/insiders), so a follower of an old link is
  // judged by the same rule as the page it lands on.
  //
  // Derived from the API, as this table requires: every GTM page's reads carry
  // `@Permissions(InternalGtmView)` — `gtm-internal.controller.ts` (Articles),
  // `gtm-sales.controller.ts`, `gtm-outreach.controller.ts`, `seo-internal.controller.ts`, and
  // the Affiliates reads in `referrals.internal.controller.ts` (`dashboard`,
  // `insiders/firstpromoter`). `api/tests/unit/gtm-permission.guard.spec.ts` pins that. Trade
  // shows and the Channels overview call no gated API of their own, so they simply take the
  // group's rule.
  //
  // Until VRG-684 these were seven entries on `internal_analytics_view`, a placeholder recorded
  // in VRG-672. Analytics keeps that permission; GTM no longer borrows it.
  { path: ROUTES.INTERNAL_GTM, permission: 'internal_gtm_view' },

  // `/internal/ask-stats` is a REDIRECT to `/internal/analytics/product?tab=ask` (App.tsx), kept
  // because published guides and the glossary link to it. Same rule as the tab it lands on
  // (`internal_analytics_view` since VRG-685; it was `internal_support_manage`).
  { path: ROUTES.INTERNAL_ASK_STATS, permission: 'internal_analytics_view' },

  // ── Accounts ───────────────────────────────────────────────────────────────
  // `GET companies/admin` — `@Permissions(PermissionName.ViewCompanies)`
  // (`infrastructure/controllers/company.admin.controller.ts:431-438`). All four
  // internal roles hold `view_companies`, which is what makes `/internal/companies`
  // a valid post-auth landing for every one of them.
  { path: ROUTES.INTERNAL_COMPANIES, permission: 'view_companies' },

  // `demos/admin` — every route `@Permissions(PermissionName.DemoProvision)`
  // (`infrastructure/controllers/demo.admin.controller.ts:177-179, 238-250`).
  { path: ROUTES.INTERNAL_DEMOS, permission: 'demo_provision' },

  // `internal/signups` is role-gated with no `@Permissions`
  // (`infrastructure/controllers/signups.internal.controller.ts:139-142`), and
  // `SignupsPage` carries its own platform_admin guard because it graduated out of
  // Labs (App.tsx:580-586). Recorded as roles rather than left open, so the URL and
  // the nav agree with the page's own guard instead of each deciding separately.
  { path: ROUTES.INTERNAL_SIGNUPS, permission: null, roles: ['platform_admin'] },
  // Access (VRG-727; "People" until 2026-10-20) replaces Signups (same guard: the API's
  // `internal/access` routes are RequireAdmin PlatformAdmin, people-access-internal.controller.ts).
  // The two old addresses that now redirect to it keep entries so a non-admin is not shown
  // them; the classic page keeps its own so its URL and its guard still agree.
  { path: ROUTES.INTERNAL_ACCESS, permission: null, roles: ['platform_admin'] },
  { path: ROUTES.INTERNAL_PEOPLE, permission: null, roles: ['platform_admin'] },
  { path: ROUTES.INTERNAL_SIGNUPS_CLASSIC, permission: null, roles: ['platform_admin'] },

  // `GET internal/subscriptions` — `@Permissions(PermissionName.InternalSubscriptionView)`,
  // roles `[PlatformAdmin, PlatformManager, VeraFinance]`
  // (`infrastructure/controllers/subscription.internal.controller.ts`).
  //
  // VRG-426 closed both halves of the gap this entry used to describe. The controller now
  // carries the decorator (it had none, so the permission gated only this table), and
  // `platform_manager` is granted `internal_subscription_view` — which also repairs the
  // regression this entry itself caused, where gating the row on a permission manager did
  // not hold silently took the page out of its nav.
  { path: ROUTES.INTERNAL_SUBSCRIPTIONS, permission: 'internal_subscription_view' },

  // ── Commerce ───────────────────────────────────────────────────────────────
  // `shop/orders` — `@Permissions(PermissionName.InternalOrderView)` on every read,
  // `InternalOrderLink` on the writes, roles `[PlatformAdmin, VeraFulfillment]`
  // (`infrastructure/controllers/shopify-orders.controller.ts:45-50, 229-234`). This is
  // the one place the API, the seed and this table already agreed.
  { path: ROUTES.INTERNAL_ORDERS, permission: 'internal_order_view' },

  // `internal/invoices` — `@Permissions(PermissionName.InternalInvoiceView)` on all three
  // routes (`infrastructure/controllers/invoices.internal.controller.ts`).
  //
  // VRG-426 — this was the ONE `{ roles }` entry that existed because the catalog had no
  // permission to express it. `internal_invoice_view` now exists and is seeded to
  // platform_admin + platform_manager (both already reached the page, so nothing is taken
  // away) + vera_finance, which gains the row: invoices are the artifact of the refunds
  // and plan changes Finance already owns, and it was the one internal role locked out.
  { path: ROUTES.INTERNAL_INVOICES, permission: 'internal_invoice_view' },

  // `GET packages` (the list) — `@Permissions(PermissionName.InternalPackageView)` as of
  // VRG-426; every act on the page (register, fill, encode, bulk upload) is
  // `@Permissions(PermissionName.InternalPackageUpload)`
  // (`infrastructure/controllers/package.controller.ts`).
  //
  // The "no controller enforces it today" caveat this entry used to carry is gone: the
  // list route now enforces `internal_package_view` itself, so the page's rule is read
  // off the API rather than asserted here.
  { path: ROUTES.INTERNAL_PACKAGES, permission: 'internal_package_view' },

  // `GET packages/tags` — `@Permissions(PermissionName.InternalPackageUpload)`
  // (`infrastructure/controllers/package.controller.ts:488-492`).
  { path: ROUTES.INTERNAL_TAGS, permission: 'internal_package_upload' },

  // ── Operations ─────────────────────────────────────────────────────────────
  // `GET admins/admin/platform-supervisors` —
  // `@Permissions(PermissionName.ViewPlatformSupervisors)`
  // (`infrastructure/controllers/admins.admin.controller.ts:181-190`). Seeded to
  // platform_admin only (`seed.ts:565`).
  { path: ROUTES.INTERNAL_USERS, permission: 'view_platform_supervisors' },

  // `internal/integration-requests` — `@Permissions(PermissionName.InternalIntegrationsView)`
  // (`features/integration-requests/integration-requests-internal.controller.ts:52-57`).
  { path: ROUTES.INTERNAL_INTEGRATIONS, permission: 'internal_integrations_view' },

  // `internal/support` — `@Permissions(PermissionName.InternalSupportManage)` on every
  // route (`features/support/support-internal.controller.ts:80-82`).
  { path: ROUTES.INTERNAL_SUPPORT, permission: 'internal_support_manage' },

  // `internal/announcements` — `@Permissions(PermissionName.InternalAnnouncementsManage)`
  // on every route (`features/announcements/announcements-internal.controller.ts:58-63`).
  { path: ROUTES.INTERNAL_ANNOUNCEMENTS, permission: 'internal_announcements_manage' },

  // `/internal/content` (the platform route; the API is still `internal/market-items` —
  // only the UI was renamed) — `@Permissions(PermissionName.InternalMarketIntelView)` on the
  // list route (`features/market-data/market-items-internal.controller.ts`). The
  // stricter `internal_market_intel_manage` gates the write routes only; reaching the
  // page at all just needs view.
  { path: ROUTES.INTERNAL_MARKET, permission: 'internal_market_intel_view' },

  // `/internal/content/sources` — VRG-462. Same VIEW permission as the corpus: reading
  // which feeds are registered is not a stronger capability than reading what they
  // produced. The write routes carry `internal_market_intel_manage`
  // (`features/market-data/market-sources-internal.controller.ts`).
  { path: ROUTES.INTERNAL_MARKET_SOURCES, permission: 'internal_market_intel_view' },

  // `/internal/content/review` — VRG-463. MANAGE, not view, and deliberately stricter
  // than its siblings: a proposed stat is unreviewed model output about a third
  // party's figures, so the population that can see one should be exactly the
  // population that can reject it. Matches
  // `features/market-data/market-stats-internal.controller.ts`, where even the reads
  // carry `internal_market_intel_manage`.
  { path: ROUTES.INTERNAL_MARKET_REVIEW, permission: 'internal_market_intel_manage' },

  // `/internal/competitors` — VRG-744, Competitor research. `internal_gtm_view`, the SEO gate,
  // because the competitor record it shows has always lived behind GTM → SEO (seo_competitors,
  // `seo-internal.controller.ts`), and the new tabs keep that audience
  // (`competitor-research.controller.ts`: reads `@Permissions(InternalGtmView)`, writes
  // platform_admin only). No new permission and no Sales data. The Content tab additionally needs
  // `internal_market_intel_view`, server-side.
  { path: ROUTES.INTERNAL_COMPETITORS, permission: 'internal_gtm_view' },

  // ── Product (was "Tools", the unlabelled bottom block — VRG-672) ─────────────
  // `internal/labs` is role-gated `[PlatformAdmin]` with no `@Permissions`
  // (`infrastructure/controllers/labs.controller.ts:86-87`), and `LabsPage` already
  // bounces non-admins itself. Recorded so the nav, the URL and the page agree.
  { path: ROUTES.INTERNAL_LABS, permission: null, roles: ['platform_admin'] },

  // ── Deliberately ungated ───────────────────────────────────────────────────
  // The staff docs site serves a committed artifact and calls no cross-company API, so
  // any internal role may read it. Listed rather than left to the default so that
  // "ungated" reads as a decision.
  { path: ROUTES.INTERNAL_DOCS, permission: null },
]

/** Longest path first, so `/internal/orders/:id` never matches the `/internal` entry. */
const SORTED_ENTRIES = [...INTERNAL_ACCESS_ENTRIES].sort(
  (a, b) => b.path.length - a.path.length,
)

/** Reachable by any internal role — the default for an unlisted `/internal/*` path. */
const OPEN_TO_ALL_INTERNAL: InternalAccessRule = { permission: null }

/**
 * The rule guarding `pathname`.
 *
 * Matched on SEGMENT boundaries, not string prefix, so a future `/internal/orders-x`
 * cannot inherit `/internal/orders`' rule — the same trap `ReferrerBackLink`'s ancestry
 * check documents for `/tags` vs `/tags-shop` (§42).
 */
export const ruleForInternalPath = (pathname: string): InternalAccessRule => {
  const path = pathname.replace(/\/+$/, '') || '/'
  const match = SORTED_ENTRIES.find((entry) =>
    entry.exact ? path === entry.path : path === entry.path || path.startsWith(`${entry.path}/`),
  )
  return match ?? OPEN_TO_ALL_INTERNAL
}

/** Does a user with this role name and permission set satisfy `rule`? */
export const satisfiesInternalRule = (
  rule: InternalAccessRule,
  roleName: string | undefined | null,
  grantedPermissions: ReadonlySet<string>,
): boolean => {
  if (rule.roles && !(roleName && rule.roles.includes(roleName))) return false
  if (rule.permission && !grantedPermissions.has(rule.permission)) return false
  // AND across the shapes, OR within `anyPermission` — a rule may carry both, in which
  // case `permission` is the floor everyone needs and `anyPermission` is "plus at least
  // one of these". No current entry does, but the semantics have to be stated or the
  // first one to try it gets whichever reading the code happened to have.
  if (rule.anyPermission && !rule.anyPermission.some((p) => grantedPermissions.has(p))) {
    return false
  }
  return true
}
